import type { BaseIssue, BaseSchema, LiteralSchema, UnionSchema } from 'valibot'
import type { AppearanceModule } from '@setplex/tria-api'
import type { MenuItemDto } from '@setplex/wbs-api-types'
import type { ApiType } from '../../../shared/schemas'
import * as v from 'valibot'
import { not_empty_array, safe_array } from '../../../shared/schemas'

/**
 * List of standard modules in the app,
 * which has been existing before appearance modules feature
 */
const STATIC_MODULES = [
  'home',
  'tv_channels',
  'movie',
  'tv_shows',
  'epg',
  'catchups',
  'events',
  'search',
  'my_list',
  'settings',
]

/**
 * List of modules, which should be omitted from the menu,
 * because they are handled in other places of the app
 */
const OMIT_MODULES = ['search', 'my_list', 'settings']

export const LeafModuleSchema = v.pipe(
  v.object({
    title: v.string(),
    localizedTitleKey: v.nullish(v.string(), undefined),
    identifier: v.string(),
    iconName: v.string(),

    // `type` is validated in the next rawTransform step
    type: v.string() as any as UnionSchema<
      [LiteralSchema<'static', undefined>, LiteralSchema<'dynamic', undefined>],
      undefined
    >,
  }),
  v.rawTransform<any, AppearanceModule>(({ dataset, addIssue, NEVER }) => {
    const mod: AppearanceModule = dataset.value

    // standard module, which has ststic implementation
    if (STATIC_MODULES.includes(mod.identifier)) {
      // unknown type, fallback to static implementation
      if (mod.type !== 'static' && mod.type !== 'dynamic') {
        return { ...mod, type: 'static' }
      }

      // handle home module as static only
      if (mod.identifier === 'home') {
        if (mod.type !== 'static') {
          return { ...mod, type: 'static' }
        }
      }
    }

    // custom module, which can be only dynamic
    else {
      if (mod.type !== 'dynamic') {
        addIssue({ message: `Unknown module type: ${mod.type}` })
        return NEVER
      }
    }

    return mod
  })
) satisfies ApiType<MenuItemDto, AppearanceModule>

export const GroupModuleSchema = v.intersect([
  LeafModuleSchema,
  v.object({
    modules: modules(LeafModuleSchema),
  }),
]) satisfies ApiType<MenuItemDto, AppearanceModule>

export const ModulesSchema = modules(
  v.union([GroupModuleSchema, LeafModuleSchema])
) satisfies ApiType<MenuItemDto[] | undefined, AppearanceModule[] | undefined>

/**
 * Schema to safely handle array of modules,
 * any unknown or invalid module will be skipped with warning in console
 */
function modules<
  const TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(schema: TSchema) {
  return v.nullish(
    not_empty_array(
      v.pipe(
        safe_array(schema, (value, error) => {
          console.warn('Module', value, 'omitted:', error)
        }),

        // filter out module, because they has separate place in the app
        v.transform((arr) =>
          arr.filter((m: any) => !OMIT_MODULES.includes(m.identifier))
        )
      ),
      undefined
    ),
    undefined
  )
}
